import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RightPanelTabs } from "../../../constants/Panels";
import { LighthouseContext } from "../../../Context";
import { changeRightPanelTab, setHasUnsavedChanges, setRightPanelActiveTab } from "../../../store/appSlice";
import {
    setIsSectorEditorOpen,
    setIsSectorEditTableOpen,
    setIsShowingIALAWarning,
} from "../../../store/lighthouseSlice";
import { StoreState } from "../../../store/rootReducer";
import { getMapView, removedAndDiscontinuedDefExpr } from "../../../utils/arcgisUtils";
import SectorEditorContent from "./SectorEditorContent";
import usePrevious from "../../../utils/usePrevious";
import { AppDispatch } from "../../../store/storeSetup";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";

const SectorEditor = () => {
    const dispatch = useDispatch<AppDispatch>();
    const lighthouseContext = useContext(LighthouseContext);
    const isSectorEditorOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditorOpen);
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const previousRightPanelActiveTab = usePrevious(rightPanelActiveTab);
    const selectedLineRef = lighthouseContext?.selectedLineRef.value;

    useEffect(() => {
        if (previousRightPanelActiveTab === RightPanelTabs.SECTOR && rightPanelActiveTab !== RightPanelTabs.SECTOR) {
            closeSectorEditing(false);
        }
    }, [rightPanelActiveTab]);

    const closeSectorEditing = (warn: boolean) => {
        if (hasUnsavedChanges && warn) {
            const result = dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
            if (!result) {
                // User cancelled, don't unselect
                return;
            }
        }
        dispatch(setHasUnsavedChanges(false));
        lighthouseContext?.lighthouseSectorPanelsOpen.set(new Set());
        dispatch(setIsShowingIALAWarning(false));
        dispatch(setIsSectorEditTableOpen(false));
        dispatch(setIsSectorEditorOpen(false));
        dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));

        const mapView = getMapView();
        if (!mapView?.map) return;

        const lighthouseEditingLayer = mapView?.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
        const lighthouseOverlayLayer = mapView?.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
        const sectorLayer = mapView?.map?.findLayerById("Sektorflate") as FeatureLayer;
        const sectorLineLayer = mapView?.map?.findLayerById("Sektorlinje") as FeatureLayer;
        lighthouseEditingLayer?.removeAll();
        lighthouseOverlayLayer?.removeAll();

        sectorLayer.definitionExpression = removedAndDiscontinuedDefExpr(sectorLayer, showRemovedFeatures);
        sectorLineLayer.definitionExpression = removedAndDiscontinuedDefExpr(sectorLineLayer, showRemovedFeatures);

        selectedLineRef!.current = null;
    };

    if (!isSectorEditorOpen) return null;

    return (
        <div className={`feature-viewer`}>
            <SectorEditorContent closeSectorEditing={closeSectorEditing} />
        </div>
    );
};

export default SectorEditor;
